/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { IS_TAURI } from '@/util/platform'
import { LOCAL_HOSTS, PROXY_HOSTS } from '@/core/localHosts'

export function useLiveHost(): boolean {
    // Tauri serves the bundle from a custom URI scheme and has no /proxy/*
    // server, so it must always hit the live host.
    if (IS_TAURI) return true
    // Hit the live host from NODE, or from any host that does NOT front a
    // same-origin `/proxy/*` server. A host in `PROXY_HOSTS` (local dev + the
    // self-hosted web deployment) routes through `/proxy/*` instead to dodge
    // CORS on the credentialed upstreams.
    return typeof location === 'undefined' || !PROXY_HOSTS.includes(location.hostname)
}

/**
 * Resolve a service host to its live origin, or to the same-origin `/proxy/*`
 * path when running from a local browser (see `useLiveHost`). Collapses the
 * `useLiveHost() ? <liveHost> : <proxyPath>` branch every proxied service
 * repeats.
 */
export function proxiedHost(proxyPath: string, liveHost = 'https://music.amazon.com'): string {
    return useLiveHost() ? liveHost : proxyPath
}

/**
 * True only from a local dev browser (`LOCAL_HOSTS`) — NARROWER than
 * `useLiveHost`'s `PROXY_HOSTS`, which also covers the self-hosted web deploy.
 * The claw agent uses this: resonance CORS-allows the deployed origin but not
 * localhost, so claw proxies in dev only and hits resonance directly in prod.
 */
export function useDevProxy(): boolean {
    if (IS_TAURI || typeof location === 'undefined') return false
    return LOCAL_HOSTS.includes(location.hostname)
}

/**
 * If running locally in a browser, use a proxy for CORS.
 * Otherwise, use prod - music.amazon.com
 */
export function getApiHost() {
    return proxiedHost('/proxy/music')
}

const TLD_MATCH_REGEX = /\.amazon\.([a-z.]+)$/

/**
 * The gql origin must be same-site with the page: a credentialed request to a
 * cross-site origin fails CORS (the gateway returns a wildcard ACAO). Derive the
 * TLD from the host; fall back to `.dev` off an amazon.<tld> host.
 */
export function getGqlOrigin(): string {
    const tld =
        typeof location !== 'undefined' ? location.hostname.match(TLD_MATCH_REGEX)?.[1] : undefined
    return `https://gql.music.amazon.${tld ?? 'dev'}/`
}

/**
 * DMLS host for streaming requests. Cookie-session auth (AuthPortal) sends no
 * bearer, so the request must be same-origin with the page for the browser to
 * attach the first-party session cookie; a cross-origin music.amazon.com hit
 * drops the cookie and DMLS 401s with NO_COOKIES. Bearer auth keeps the fixed
 * live host — the token travels in the Authorization header, not a cookie.
 */
export function getDmlsHost(cookieAuth: boolean): string {
    if (cookieAuth && typeof location !== 'undefined') {
        return `https://${location.host}`
    }
    return 'https://music.amazon.com'
}
